import { useState, useMemo } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { 
  FileCheck, 
  FileQuestion,
  Loader2,
  RefreshCw,
  X,
  Link2,
  Unlink,
  AlertTriangle,
  Calendar,
  Search,
  ChevronDown,
  ChevronRight,
  ArrowUpRight,
  ArrowDownLeft,
  Settings,
  Info,
  Zap
} from 'lucide-react'
import api from '../services/api'
import { useWorkspace } from '../hooks/useWorkspace'
import { useLanguage } from '../hooks/useLanguage'
import { useToast } from '../hooks/useToast'
import { ReconciliationModuleCard } from '../components/FinancialIntelligencePanel'
import { useReconciliationSuggestions } from '../hooks/useFinancialIntelligence'
import {
  buildReconciliationLookup,
} from '../services/financialIntelligence'

// ─── Types ──────────────────────────────────────────────────────────────────

interface InvoiceBankMatch {
  id: string
  invoice_id: string
  transaction_id: string
  match_type: 'auto' | 'manual' | 'suggested'
  match_score?: number
  matched_amount: number
  match_reason?: string
  matched_at: string
  matched_by?: string
}

interface Invoice {
  id: string
  source: string
  external_id?: string
  type: 'emessa' | 'ricevuta'
  number?: string
  date: string
  due_date?: string
  counterparty_name?: string
  counterparty_vat?: string
  total_amount: number
  net_amount?: number
  vat_amount?: number
  currency: string
  payment_status: 'pending' | 'partial' | 'paid' | 'overdue'
  paid_amount: number
  notes?: string
  matches: InvoiceBankMatch[]
  remaining_amount: number
  days_overdue?: number
  category_name?: string
}

interface Transaction {
  id: string
  date: string
  description: string
  amount: number
  category_id?: string
  category_name?: string
}

interface ReconciliationStats {
  invoices: {
    total: number
    paid: number
    partial: number
    pending: number
    overdue: number
    total_amount: number
    paid_amount: number
  }
  overdue: {
    count: number
    amount: number
  }
  matches: {
    total_matches: number
    auto_matches: number
    manual_matches: number
    total_matched_amount: number
  }
}

type TabType = 'automatic' | 'suggestions' | 'manual'

// ─── Component ──────────────────────────────────────────────────────────────

export default function Reconciliation() {
  const { currentWorkspace } = useWorkspace()
  const { language } = useLanguage()
  const { showToast, confirm } = useToast()
  const queryClient = useQueryClient()
  
  const [activeTab, setActiveTab] = useState<TabType>('automatic')
  const [searchQuery, setSearchQuery] = useState('')
  const [selectedInvoice, setSelectedInvoice] = useState<Invoice | null>(null)
  const [showMatchModal, setShowMatchModal] = useState(false)
  const [expandedPairs, setExpandedPairs] = useState<Set<string>>(new Set())

  // ─── API Queries ────────────────────────────────────────────────────────

  const { data: stats } = useQuery<ReconciliationStats>({
    queryKey: ['reconciliation-stats', currentWorkspace?.id],
    queryFn: async () => {
      const response = await api.get('/reconciliation/stats')
      return response.data
    },
    enabled: !!currentWorkspace?.id,
  })

  const { data: invoicesData, isLoading: invoicesLoading } = useQuery<{
    invoices: Invoice[]
    total: number
  }>({
    queryKey: ['invoices', currentWorkspace?.id, searchQuery],
    queryFn: async () => {
      const params = new URLSearchParams()
      if (searchQuery) params.set('search', searchQuery)
      params.set('pageSize', '200')
      const response = await api.get(`/invoices?${params.toString()}`)
      return response.data
    },
    enabled: !!currentWorkspace?.id,
  })

  const { data: transactionsData } = useQuery<{ data?: Transaction[]; transactions?: Transaction[] }>({
    queryKey: ['transactions-for-match', currentWorkspace?.id],
    queryFn: async () => {
      const response = await api.get('/transactions/?per_page=500')
      return response.data
    },
    enabled: !!currentWorkspace?.id && (showMatchModal || activeTab === 'automatic'),
  })

  const allTransactions = useMemo(() => {
    if (!transactionsData) return []
    return transactionsData.data || transactionsData.transactions || []
  }, [transactionsData])

  const reconciliationLookup = useMemo(
    () => buildReconciliationLookup(allTransactions, invoicesData?.invoices || []),
    [allTransactions, invoicesData?.invoices],
  )

  const canonicalReconciliationPayload = useMemo(() => {
    if (!currentWorkspace?.id) return null
    return {
      ensure_backfill: true,
      transaction_limit: 80,
      invoice_limit: 40,
      auto_match_threshold: 0.92,
      suggest_threshold: 0.72,
      model_version: 'lightgbm-v0',
    }
  }, [currentWorkspace?.id])

  const aiSuggestionsQuery = useReconciliationSuggestions({
    enabled: !!currentWorkspace?.id,
    source: 'canonical',
    canonicalPayload: canonicalReconciliationPayload,
    lookup: reconciliationLookup,
  })

  // ─── Mutations ──────────────────────────────────────────────────────────

  const runReconciliationMutation = useMutation({
    mutationFn: async () => {
      const response = await api.post('/reconciliation/run')
      return response.data
    },
    onSuccess: (data) => {
      showToast('success', `${language === 'it' ? 'Riconciliazione completata' : 'Reconciliation complete'}: ${data.stats?.auto_matched || 0} ${language === 'it' ? 'abbinamenti automatici' : 'auto matches'}`)
      queryClient.invalidateQueries({ queryKey: ['invoices'] })
      queryClient.invalidateQueries({ queryKey: ['reconciliation-stats'] })
    },
    onError: () => {
      showToast('error', language === 'it' ? 'Errore durante la riconciliazione' : 'Reconciliation error')
    },
  })

  const matchMutation = useMutation({
    mutationFn: async ({ invoiceId, transactionId, amount }: { invoiceId: string; transactionId: string; amount?: number }) => {
      const response = await api.post(`/invoices/${invoiceId}/match`, {
        transaction_id: transactionId,
        matched_amount: amount,
      })
      return response.data
    },
    onSuccess: () => {
      showToast('success', language === 'it' ? 'Abbinamento creato' : 'Match created')
      setShowMatchModal(false)
      setSelectedInvoice(null)
      queryClient.invalidateQueries({ queryKey: ['invoices'] })
      queryClient.invalidateQueries({ queryKey: ['reconciliation-stats'] })
    },
    onError: () => {
      showToast('error', language === 'it' ? 'Errore abbinamento' : 'Match error')
    },
  })

  const deleteMatchMutation = useMutation({
    mutationFn: async (matchId: string) => {
      await api.delete(`/matches/${matchId}`)
    },
    onSuccess: () => {
      showToast('success', language === 'it' ? 'Abbinamento rimosso' : 'Match removed')
      queryClient.invalidateQueries({ queryKey: ['invoices'] })
      queryClient.invalidateQueries({ queryKey: ['reconciliation-stats'] })
    },
  })

  // ─── Computed ───────────────────────────────────────────────────────────

  const { matched, suggested, unmatched } = useMemo(() => {
    const invoices = invoicesData?.invoices || []
    const m: Invoice[] = []
    const s: Invoice[] = []
    const u: Invoice[] = []
    for (const inv of invoices) {
      if (inv.payment_status === 'paid' || inv.matches.length > 0) m.push(inv)
      else if (inv.payment_status === 'overdue') s.push(inv)
      else u.push(inv)
    }
    return { matched: m, suggested: s, unmatched: u }
  }, [invoicesData])

  const currentList = activeTab === 'automatic' ? matched : activeTab === 'suggestions' ? suggested : unmatched
  const suggestionsCount = suggested.length + unmatched.length

  // ─── Helpers ────────────────────────────────────────────────────────────

  const formatCurrency = (amount: number) =>
    new Intl.NumberFormat('it-IT', { style: 'currency', currency: 'EUR' }).format(amount)

  const formatDate = (d: string) =>
    new Date(d).toLocaleDateString(language === 'it' ? 'it-IT' : 'en-GB', { day: '2-digit', month: 'short', year: 'numeric' })

  const togglePair = (id: string) => {
    setExpandedPairs(prev => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })
  }

  const handleDeleteMatch = async (matchId: string) => {
    const confirmed = await confirm({
      title: language === 'it' ? 'Rimuovi abbinamento' : 'Remove match',
      message: language === 'it' ? 'Sei sicuro di voler rimuovere questo abbinamento?' : 'Are you sure you want to remove this match?',
      confirmText: language === 'it' ? 'Rimuovi' : 'Remove',
      type: 'danger'
    })
    if (confirmed) deleteMatchMutation.mutate(matchId)
  }

  // ─── Render ─────────────────────────────────────────────────────────────

  return (
    <div className="space-y-0 pt-1">
      {/* Page Header — Agicap style */}
      <div className="flex items-center justify-between pb-4 border-b border-gray-200 dark:border-slate-700">
        <div className="flex items-center gap-2">
          <h1 className="text-lg font-semibold text-gray-900 dark:text-white">
            {language === 'it' ? 'Riconciliazione' : 'Reconciliation'}
          </h1>
          <button className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300">
            <Info className="w-4 h-4" />
          </button>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => runReconciliationMutation.mutate()}
            disabled={runReconciliationMutation.isPending}
            className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-200 bg-white dark:bg-slate-800 border border-gray-300 dark:border-slate-600 rounded-lg hover:bg-gray-50 dark:hover:bg-slate-700 transition-colors disabled:opacity-50"
          >
            {runReconciliationMutation.isPending ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : (
              <RefreshCw className="w-4 h-4" />
            )}
            {language === 'it' ? 'Riconcilia' : 'Reconcile'}
          </button>
          <button className="p-2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 rounded-lg hover:bg-gray-100 dark:hover:bg-slate-700">
            <Settings className="w-4 h-4" />
          </button>
        </div>
      </div>

      {/* Tabs — Agicap style underline tabs */}
      <div className="flex items-center gap-6 border-b border-gray-200 dark:border-slate-700">
        {([
          { key: 'automatic' as TabType, label: language === 'it' ? 'Automatica' : 'Automatic' },
          { key: 'suggestions' as TabType, label: language === 'it' ? 'Suggerimenti' : 'Suggestions' },
          { key: 'manual' as TabType, label: language === 'it' ? 'Manuale' : 'Manual' },
        ] as const).map(tab => (
          <button
            key={tab.key}
            onClick={() => setActiveTab(tab.key)}
            className={`relative py-3 text-sm font-medium transition-colors flex items-center gap-2 ${
              activeTab === tab.key
                ? 'text-blue-600 dark:text-blue-400'
                : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'
            }`}
          >
            {tab.label}
            {tab.key === 'suggestions' && suggestionsCount > 0 && (
              <span className="w-2 h-2 rounded-full bg-red-500 animate-pulse" />
            )}
            {activeTab === tab.key && (
              <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-blue-600 dark:bg-blue-400 rounded-full" />
            )}
          </button>
        ))}
      </div>

      <div className="pt-4">
        <ReconciliationModuleCard
          data={aiSuggestionsQuery.data}
          lookup={aiSuggestionsQuery.lookup}
          isLoading={aiSuggestionsQuery.isLoading}
          isFetching={aiSuggestionsQuery.isFetching}
          error={aiSuggestionsQuery.error}
          onRetry={() => {
            void aiSuggestionsQuery.refetch()
          }}
          subtitle="Candidati generati dal modulo AI sulla stessa base dati usata dalla riconciliazione operativa."
        />
      </div>

      {/* Content */}
      <div className="flex gap-0 mt-0">
        {/* Left sidebar — filter / stats */}
        <div className="w-52 flex-shrink-0 border-r border-gray-200 dark:border-slate-700 pr-4 pt-4 hidden md:block">
          {/* Stats summary */}
          <div className="space-y-3 mb-4">
            <div className="flex items-center justify-between">
              <span className="text-xs text-gray-500 dark:text-gray-400">
                {language === 'it' ? 'Abbinati' : 'Matched'}
              </span>
              <span className="text-xs font-semibold text-green-600 dark:text-green-400">
                {stats?.matches.total_matches || 0}
              </span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-xs text-gray-500 dark:text-gray-400">
                {language === 'it' ? 'In attesa' : 'Pending'}
              </span>
              <span className="text-xs font-semibold text-amber-600 dark:text-amber-400">
                {(stats?.invoices.pending || 0) + (stats?.invoices.partial || 0)}
              </span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-xs text-gray-500 dark:text-gray-400">
                {language === 'it' ? 'Scaduti' : 'Overdue'}
              </span>
              <span className="text-xs font-semibold text-red-600 dark:text-red-400">
                {stats?.overdue.count || 0}
              </span>
            </div>
          </div>

          <div className="border-t border-gray-200 dark:border-slate-700 pt-3">
            <p className="text-[10px] font-medium text-gray-400 dark:text-gray-500 uppercase tracking-wider mb-2">
              {language === 'it' ? 'Importo abbinato' : 'Matched amount'}
            </p>
            <p className="text-sm font-semibold text-gray-900 dark:text-white">
              {formatCurrency(stats?.matches.total_matched_amount || 0)}
            </p>
          </div>

          {/* Search */}
          <div className="mt-4">
            <div className="relative">
              <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-400" />
              <input
                type="text"
                placeholder={language === 'it' ? 'Cerca...' : 'Search...'}
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-8 pr-3 py-2 text-xs border border-gray-200 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-800 text-gray-900 dark:text-white placeholder-gray-400 focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>
          </div>
          
          {/* Info box */}
          <div className="mt-6 p-3 bg-blue-50 dark:bg-blue-900/20 rounded-lg border border-blue-100 dark:border-blue-800">
            <div className="flex items-start gap-2">
              <Zap className="w-4 h-4 text-blue-500 flex-shrink-0 mt-0.5" />
              <p className="text-[11px] text-blue-700 dark:text-blue-300 leading-relaxed">
                {language === 'it' 
                  ? 'La riconciliazione confronta automaticamente le transazioni bancarie con gli incassi e le uscite previsti.' 
                  : 'Reconciliation automatically matches bank transactions with expected income and expenses.'}
              </p>
            </div>
          </div>
        </div>

        {/* Main content — transaction pairs */}
        <div className="flex-1 pl-0 md:pl-4 pt-4">
          {/* Last sync info */}
          <div className="flex items-center justify-between mb-4">
            <p className="text-xs text-gray-400 dark:text-gray-500">
              {language === 'it' ? 'Transazioni previste' : 'Expected transactions'} 
              <span className="ml-2 px-1.5 py-0.5 bg-gray-100 dark:bg-slate-700 rounded text-gray-600 dark:text-gray-300 font-medium">
                {currentList.length}
              </span>
            </p>
            <p className="text-xs text-gray-400 dark:text-gray-500">
              {language === 'it' ? 'Ultima sincronizzazione' : 'Last synchronization'}: {new Date().toLocaleDateString(language === 'it' ? 'it-IT' : 'en-GB', { day: 'numeric', month: 'long', year: 'numeric' })}
            </p>
          </div>

          {/* Transaction pairs / list */}
          {invoicesLoading ? (
            <div className="flex items-center justify-center py-16">
              <Loader2 className="w-8 h-8 animate-spin text-blue-500" />
            </div>
          ) : currentList.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-16 px-6">
              <FileCheck className="w-12 h-12 text-slate-300 dark:text-slate-600 mb-4" />
              <p className="text-sm font-medium text-gray-900 dark:text-white mb-1">
                {activeTab === 'automatic' 
                  ? (language === 'it' ? 'Nessun abbinamento trovato' : 'No matches found')
                  : (language === 'it' ? 'Nessuna transazione in attesa' : 'No pending transactions')
                }
              </p>
              <p className="text-xs text-gray-400 dark:text-gray-500 text-center max-w-sm mb-4">
                {language === 'it' 
                  ? 'Per la riconciliazione servono sia fatture che movimenti bancari. Puoi:'
                  : 'Reconciliation needs both invoices and bank transactions. You can:'}
              </p>
              <div className="text-xs text-gray-500 dark:text-gray-400 space-y-1.5 text-left">
                <p>• {language === 'it' ? 'Connettere Fatture in Cloud (Contabilità → Fatture)' : 'Connect Fatture in Cloud (Accounting → Invoices)'}</p>
                <p>• {language === 'it' ? 'Inserire fatture manualmente dalla pagina Fatture' : 'Enter invoices manually from the Invoices page'}</p>
                <p>• {language === 'it' ? 'Caricare movimenti bancari dalla pagina Upload' : 'Upload bank statements from the Upload page'}</p>
              </div>
            </div>
          ) : (
            <div className="space-y-2">
              {currentList.map(invoice => {
                const isExpanded = expandedPairs.has(invoice.id)
                const hasMatch = invoice.matches.length > 0
                
                // Find matching transaction from loaded data
                const matchedTransaction = hasMatch 
                  ? allTransactions.find(t => t.id === invoice.matches[0]?.transaction_id)
                  : undefined

                return (
                  <div key={invoice.id} className="rounded-xl border border-gray-200 dark:border-slate-700 bg-white dark:bg-slate-800 overflow-hidden shadow-sm">
                    {/* Invoice row — "expected" transaction */}
                    <div 
                      className={`flex items-center gap-3 px-4 py-3 cursor-pointer hover:bg-gray-50 dark:hover:bg-slate-700/50 transition-colors ${
                        hasMatch ? 'border-l-4 border-l-blue-500' : ''
                      }`}
                      onClick={() => togglePair(invoice.id)}
                    >
                      {/* Expand icon */}
                      <div className="text-gray-400">
                        {isExpanded ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
                      </div>
                      
                      {/* Type icon */}
                      <div className={`p-1.5 rounded-md ${
                        invoice.type === 'emessa' 
                          ? 'bg-green-100 dark:bg-green-900/30' 
                          : 'bg-orange-100 dark:bg-orange-900/30'
                      }`}>
                        {invoice.type === 'emessa' 
                          ? <ArrowUpRight className="w-4 h-4 text-green-600 dark:text-green-400" /> 
                          : <ArrowDownLeft className="w-4 h-4 text-orange-600 dark:text-orange-400" />
                        }
                      </div>

                      {/* Invoice info */}
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <p className="text-sm font-medium text-gray-900 dark:text-white truncate">
                            {invoice.counterparty_name || invoice.number || 'N/A'}
                          </p>
                          {invoice.payment_status === 'overdue' && (
                            <span className="flex items-center gap-1 px-1.5 py-0.5 text-[10px] font-medium bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-400 rounded">
                              <AlertTriangle className="w-3 h-3" />
                              {language === 'it' ? 'Scaduto' : 'Overdue'}
                            </span>
                          )}
                          {hasMatch && (
                            <span className="flex items-center gap-1 px-1.5 py-0.5 text-[10px] font-medium bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400 rounded">
                              <Link2 className="w-3 h-3" />
                              {language === 'it' ? 'Abbinato' : 'Matched'}
                            </span>
                          )}
                        </div>
                        <div className="flex items-center gap-2 mt-0.5">
                          <span className="text-xs text-gray-400 dark:text-gray-500">
                            {formatDate(invoice.date)}
                          </span>
                          <span className="text-gray-300 dark:text-gray-600">·</span>
                          <span className="text-xs text-gray-400 dark:text-gray-500">
                            {language === 'it' ? 'Transazione prevista' : 'Expected transaction'}
                          </span>
                          {invoice.category_name && (
                            <>
                              <span className="text-gray-300 dark:text-gray-600">·</span>
                              <span className="inline-flex items-center text-xs text-gray-500 dark:text-gray-400 bg-gray-100 dark:bg-slate-700 px-1.5 py-0.5 rounded">
                                {invoice.category_name}
                              </span>
                            </>
                          )}
                        </div>
                      </div>

                      {/* Amount */}
                      <p className={`text-sm font-semibold tabular-nums ${
                        invoice.type === 'ricevuta' ? 'text-red-600 dark:text-red-400' : 'text-green-600 dark:text-green-400'
                      }`}>
                        {invoice.type === 'ricevuta' ? '-' : '+'}{formatCurrency(invoice.total_amount)}
                      </p>
                    </div>

                    {/* Expanded: matched bank transaction */}
                    {isExpanded && (
                      <div className="border-t border-gray-100 dark:border-slate-700">
                        {hasMatch ? (
                          <div className="px-4 py-3 bg-gray-50 dark:bg-slate-800/80">
                            {/* Matched transaction */}
                            <div className="flex items-center gap-3 mb-2">
                              <div className="w-4" /> {/* spacer for alignment */}
                              <div className="p-1.5 rounded-md bg-blue-100 dark:bg-blue-900/30">
                                <Zap className="w-4 h-4 text-blue-600 dark:text-blue-400" />
                              </div>
                              <div className="flex-1 min-w-0">
                                <p className="text-sm font-medium text-gray-900 dark:text-white truncate">
                                  {matchedTransaction?.description || invoice.counterparty_name || invoice.number}
                                </p>
                                <p className="text-xs text-gray-400 dark:text-gray-500">
                                  {invoice.matches[0]?.matched_at ? formatDate(invoice.matches[0].matched_at) : ''} 
                                  {' — '}
                                  {invoice.matches[0]?.match_type === 'auto' 
                                    ? (language === 'it' ? 'Abbinamento automatico' : 'Auto match')
                                    : (language === 'it' ? 'Abbinamento manuale' : 'Manual match')
                                  }
                                </p>
                              </div>
                              <p className={`text-sm font-semibold tabular-nums ${
                                invoice.type === 'ricevuta' ? 'text-red-600 dark:text-red-400' : 'text-green-600 dark:text-green-400'
                              }`}>
                                {invoice.type === 'ricevuta' ? '-' : '+'}{formatCurrency(invoice.matches[0]?.matched_amount || invoice.total_amount)}
                              </p>
                            </div>

                            {/* Match info bar */}
                            <div className="flex items-center justify-between ml-10 mt-2">
                              <p className="text-[11px] text-gray-400 dark:text-gray-500">
                                {language === 'it'
                                  ? 'Abbinamento per importo, data e conto bancario secondo le impostazioni di tolleranza'
                                  : 'Match on amount, date and bank account according to your tolerance settings'}
                              </p>
                              <button
                                onClick={(e) => {
                                  e.stopPropagation()
                                  handleDeleteMatch(invoice.matches[0].id)
                                }}
                                className="flex items-center gap-1 px-2 py-1 text-xs text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 rounded transition-colors"
                              >
                                <Unlink className="w-3 h-3" />
                                {language === 'it' ? 'Rimuovi' : 'Remove'}
                              </button>
                            </div>
                          </div>
                        ) : (
                          <div className="px-4 py-3 bg-amber-50/50 dark:bg-amber-900/10">
                            <div className="flex items-center justify-between">
                              <div className="flex items-center gap-2">
                                <FileQuestion className="w-4 h-4 text-amber-500" />
                                <p className="text-xs text-amber-700 dark:text-amber-400">
                                  {language === 'it' ? 'Nessun movimento bancario abbinato' : 'No bank movement matched'}
                                </p>
                              </div>
                              <button
                                onClick={(e) => {
                                  e.stopPropagation()
                                  setSelectedInvoice(invoice)
                                  setShowMatchModal(true)
                                }}
                                className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-blue-600 dark:text-blue-400 hover:bg-blue-50 dark:hover:bg-blue-900/20 rounded-lg transition-colors"
                              >
                                <Link2 className="w-3.5 h-3.5" />
                                {language === 'it' ? 'Abbina manualmente' : 'Match manually'}
                              </button>
                            </div>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                )
              })}
            </div>
          )}
        </div>
      </div>

      {/* Manual Match Modal */}
      {showMatchModal && selectedInvoice && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm">
          <div className="bg-white dark:bg-slate-800 rounded-2xl shadow-2xl max-w-2xl w-full mx-4 max-h-[80vh] overflow-hidden">
            <div className="p-4 border-b border-gray-200 dark:border-slate-700">
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="text-sm font-semibold text-gray-900 dark:text-white">
                    {language === 'it' ? 'Abbinamento manuale' : 'Manual matching'}
                  </h3>
                  <p className="mt-0.5 text-xs text-gray-500 dark:text-gray-400">
                    {selectedInvoice.counterparty_name || selectedInvoice.number} — {formatCurrency(selectedInvoice.total_amount)}
                  </p>
                </div>
                <button
                  onClick={() => { setShowMatchModal(false); setSelectedInvoice(null) }}
                  className="p-1.5 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 rounded-lg hover:bg-gray-100 dark:hover:bg-slate-700"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
            </div>
            
            <div className="p-4">
              {/* Search in modal */}
              <div className="relative mb-4">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                <input
                  type="text"
                  placeholder={language === 'it' ? 'Cerca transazione...' : 'Search transaction...'}
                  className="w-full pl-10 pr-4 py-2.5 text-sm border border-gray-200 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-700 text-gray-900 dark:text-white placeholder-gray-400 focus:ring-1 focus:ring-blue-500"
                />
              </div>

              <div className="overflow-y-auto max-h-80 space-y-1">
                {allTransactions
                  .filter(t => {
                    const isIncome = t.amount > 0
                    return selectedInvoice.type === 'emessa' ? isIncome : !isIncome
                  })
                  .slice(0, 50)
                  .map(transaction => (
                    <button
                      key={transaction.id}
                      onClick={() => matchMutation.mutate({
                        invoiceId: selectedInvoice.id,
                        transactionId: transaction.id,
                      })}
                      disabled={matchMutation.isPending}
                      className="w-full flex items-center justify-between p-3 text-left border border-gray-100 dark:border-slate-700 rounded-lg hover:bg-blue-50 dark:hover:bg-blue-900/20 hover:border-blue-200 dark:hover:border-blue-700 transition-colors"
                    >
                      <div className="min-w-0 flex-1">
                        <p className="text-sm font-medium text-gray-900 dark:text-white truncate">
                          {transaction.description}
                        </p>
                        <p className="text-xs text-gray-500 dark:text-gray-400 flex items-center gap-2 mt-0.5">
                          <Calendar className="w-3 h-3" />
                          {formatDate(transaction.date)}
                          {transaction.category_name && (
                            <span className="bg-gray-100 dark:bg-slate-700 px-1.5 py-0.5 rounded">
                              {transaction.category_name}
                            </span>
                          )}
                        </p>
                      </div>
                      <p className={`text-sm font-semibold tabular-nums ml-4 ${
                        transaction.amount > 0 ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'
                      }`}>
                        {formatCurrency(transaction.amount)}
                      </p>
                    </button>
                  ))
                }
                {allTransactions.length === 0 && (
                  <div className="text-center py-8">
                    <p className="text-sm text-gray-400">
                      {language === 'it' ? 'Nessuna transazione disponibile' : 'No transactions available'}
                    </p>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
